import Form, { FormInstance } from "antd/es/form/Form";
import FormItem from "antd/es/form/FormItem";
import { createRef, PureComponent, ReactNode } from "react";
import { BlocksService } from "../../../services/blocks-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaText from "../../ui-kit/typography/Text/Text";
import "./save-custom-block-modal.scss";

interface ISaveCustomBlockModalProps {}

interface ISaveCustomBlockModalState {
  /** Where SAVE stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
}

class SaveCustomBlockModal extends PureComponent<
  ISaveCustomBlockModalProps & IWithSDProps,
  ISaveCustomBlockModalState
> {
  formRef = createRef<FormInstance>();

  constructor(props: ISaveCustomBlockModalProps & IWithSDProps) {
    super(props);
    this.state = {
      saveStatus: "idle"
    };
  }

  /**
   * SAVE's order: the ✓ goes up and is held, then the dialog closes. The saved toast stays — the
   * block lands in the Components panel's Blocks tab, which may not be open, so the toast is the
   * one place the result is said once the dialog is gone.
   */
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidUpdate(
    prevProps: Readonly<ISaveCustomBlockModalProps & IWithSDProps>,
    prevState: Readonly<ISaveCustomBlockModalState>,
    snapshot?: any
  ): void {
    if (this.props.sd.isSavingBlockAsCustomBlockModalVisible && !prevProps.sd.isSavingBlockAsCustomBlockModalVisible) {
      this.formRef.current?.resetFields();
    }
  }

  handleFormSubmit = async ({ customBlockName }) => {
    await this.saveCustomBlock(customBlockName);
  };

  onFinishFailed = (errorInfo) => {};

  saveCustomBlock = async (customBlockName: string): Promise<void> => {
    // Not while a save runs, and not during its ✓: a second press would save the block twice.
    if (!this.saveStatus.canStart) {
      return;
    }

    this.saveStatus.start();
    try {
      const getCustomBlockAndUsedStylesRes = this.props.sd.getCustomBlockAndUsedStyles();

      if (!getCustomBlockAndUsedStylesRes)
        throw new Error("Failed to save Custom Block, please contact to the customer service");

      const stringifiedJSON = JSON.stringify(getCustomBlockAndUsedStylesRes);

      await BlocksService.createCustomBlock(customBlockName, stringifiedJSON);
      KMainFunctions.displaySuccessNotification(`Block "${customBlockName}" has been saved successfully.`);
      this.props.sd.loadBlocks();
      this.saveStatus.succeed(this.handleClose);
    } catch (error) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  handleClose = () => {
    this.formRef.current?.resetFields();
    this.props.sd.handleCloseSaveBlockAsCustomBlockModal();
  };

  render(): ReactNode {
    const isSaving: boolean = this.state.saveStatus === "loading";
    const footer = (
      <KModalFooter
        onCancel={this.handleClose}
        cancelTestId="saveBlockModalCancelButton"
        submitLabel="Save"
        submitTestId="saveBlockModalSaveButton"
        onSubmit={() => {
          this.formRef.current?.submit();
        }}
        status={this.state.saveStatus}
      />
    );

    return (
      <KModal
        showModal={this.props.sd.isSavingBlockAsCustomBlockModalVisible}
        closeModal={this.handleClose}
        footer={footer}
        width={400}
        centered
        isLoading={isSaving}
        header="Save Block"
      >
        <div className="kuika-save-custom-block-modal">
          <div className="kuika-save-custom-block-modal__header">
            <KuikaModalIconWrapper iconName="components" intent="create" />
            <div className="kuika-save-custom-block-modal__title">
              <KuikaH5>Save Block</KuikaH5>
            </div>
          </div>
          <div className="kuika-save-custom-block-modal__content">
            <div className="kuika-save-custom-block-modal__description">
              <KuikaText>
                Type down the name for the new block. Once saved the block can be viewed under the blocks tab in
                Elements panel.
              </KuikaText>
            </div>

            <div className="kuika-save-custom-block-modal__form">
              <div className="kuika-save-custom-block-modal__form-item">
                <Form
                  ref={this.formRef}
                  name="basic"
                  onFinish={this.handleFormSubmit}
                  onFinishFailed={this.onFinishFailed}
                >
                  <FormItem
                    name="customBlockName"
                    rules={[
                      {
                        required: true,
                        // Rendered inside antd's generated error node, which cannot
                        // carry a test-id itself; wrapping the message text is what
                        // gives the validation error a stable hook.
                        message: <span data-testid="saveBlockNameRequiredError">Please input the block name!</span>
                      }
                    ]}
                  >
                    <KuikaInput data-testid="saveBlockNameInput" autoFocus />
                  </FormItem>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </KModal>
    );
  }

  checkNeedRender = (): boolean => {
    return false;
  };
}

const saveCustomBlockModal = withSD<ISaveCustomBlockModalProps>(SaveCustomBlockModal);
export { saveCustomBlockModal as SaveCustomBlockModal };
